import { ImgHTMLAttributes, SVGProps } from "react";

import { iconRegistry, IconName, SvgIconComponent } from "../icons/registry";

type SharedIconProps = {
  /** Render size in px (applies to both SVG and image icons). */
  size?: number | string;
  className?: string;
  /** Accessible label. When omitted, the icon is treated as decorative. */
  title?: string;
};

export type SvgIconProps = SharedIconProps &
  Omit<SVGProps<SVGSVGElement>, "width" | "height"> & {
    /** Built-in icon from `src/icons/*.svg` (via registry). */
    name?: IconName;
    /** One-off SVG React component (e.g. from `import { ReactComponent as X } from "./x.svg"`). */
    component?: SvgIconComponent;
    src?: never;
    alt?: never;
  };

type ImageIconProps = SharedIconProps &
  Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "width" | "height"> & {
    /** Image URL for non-SVG assets (png, jpg, …). */
    src: string;
    alt?: string;
    name?: never;
    component?: never;
  };

export type IconProps = SvgIconProps | ImageIconProps;

function resolveSvgComponent(props: SvgIconProps): SvgIconComponent | null {
  if (props.component) return props.component;
  if (props.name) return iconRegistry[props.name];
  return null;
}

/** Renders a registry SVG, a custom SVG component, or a raster image URL. */
export function Icon(props: IconProps) {
  const { size = 20, className, title } = props;

  if ("src" in props && props.src) {
    const { src, alt, ...imgRest } = props;
    return (
      <img
        src={src}
        alt={alt ?? title ?? ""}
        width={size}
        height={size}
        className={className}
        aria-hidden={title ? undefined : alt ? undefined : true}
        {...imgRest}
      />
    );
  }

  const svgProps = props as SvgIconProps;
  const Svg = resolveSvgComponent(svgProps);
  if (!Svg) return null;

  const {
    name: _name,
    component: _component,
    size: _size,
    className: _className,
    title: _title,
    ...svgRest
  } = svgProps;

  return (
    <Svg
      width={size}
      height={size}
      className={className}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      role={title ? "img" : undefined}
      {...svgRest}
    />
  );
}

// Password-visibility toggle icons. Both come from the registry (eye.svg / eye-off.svg) so
// they share the same style, 19x13 aspect ratio, and color.
function EyeGlyph({
  name,
  size = 20,
  className,
  title,
  ...props
}: Omit<SvgIconProps, "component" | "src"> & { name: IconName }) {
  const Svg = iconRegistry[name];
  const width = size;
  const height = Math.round((Number(size) * 13) / 19);

  return (
    <Svg
      width={width}
      height={height}
      className={className}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      role={title ? "img" : undefined}
      {...props}
    />
  );
}

export function EyeIcon(props: Omit<SvgIconProps, "name" | "component" | "src">) {
  return <EyeGlyph name="eye" {...props} />;
}

export function EyeOffIcon(props: Omit<SvgIconProps, "name" | "component" | "src">) {
  return <EyeGlyph name="eye-off" {...props} />;
}

export function DropdownIcon({
  size = 12,
  className,
  title,
  ...props
}: Omit<SvgIconProps, "name" | "component" | "src">) {
  const Svg = iconRegistry.dropdown;

  return (
    <Svg
      width={size}
      height={(Number(size) * 8) / 12}
      className={className}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      role={title ? "img" : undefined}
      {...props}
    />
  );
}
